import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid, DataGridElement } from '../../../../lib/components/compound/dataGrid';
import { Box, Typography } from '@mui/material';
import { Base64FileField, ActivationMailReceiverEnum } from '@pxtra-client-lib-legacy/gen';
import { ContractView } from '../shared/contractView';
import { Benefit } from '@pxtra-client-lib-legacy/types';
import { Select, SelectOption } from '../../../../lib/components';
import { InfoIcon } from '../../../../lib/icons';
import { TooltipComponent } from '../../../../lib/components/atomic/feedback';

export interface DataProps {
  benefit: Benefit;
  hideSettingData?: boolean;
  settingsData?: Record<string, boolean | string | undefined>;
  contractData?:
    | Record<string, string | number | boolean | null | undefined>
    | Array<Record<string, string | number | boolean | null | undefined>>;
  contract?: Base64FileField;
  contractSigned?: boolean | null;
  onPendingChanges?: (changes: Record<string, string>) => void;
}

export const setType = (key: string) => {
  switch (key) {
    case 'admin_portal':
    case 'employee_portal':
      return 'url';
    case 'enabled':
      return 'boolean';
    default:
      return 'string';
  }
};

type OptionType = Omit<SelectOption, 'value'> & {
  value: ActivationMailReceiverEnum;
};

const EditableDataGridElement: FC<{
  fieldKey: string;
  value: string | boolean;
  benefit: Benefit;
  activationMailReceiver?: string;
  receiverOptions: OptionType[];
  onFieldChange: (fieldName: string, value: string) => void;
}> = ({ fieldKey, value, benefit, activationMailReceiver, receiverOptions, onFieldChange }) => {
  const { t } = useTranslation();

  if (fieldKey === 'activation_mail_receiver' && benefit === 'jobbike') {
    return (
      <Box className="flex flex-col items-start pl-6 pt-8">
        <Typography variant="body2" className="text-grey-500 flex flex-row mb-1">
          {t('jobbike:activation_mail_receiver')}
          <TooltipComponent
            title={t('jobbike:activation_mail_receiver_info')}
            placement="bottom"
            arrow
          >
            <Typography>
              <InfoIcon color="grey.600" height="18px" />
            </Typography>
          </TooltipComponent>
        </Typography>
        <Box className="flex items-center gap-2 flex-1 justify-end">
          <Select
            className="w-[430px]"
            options={receiverOptions}
            value={activationMailReceiver}
            onChange={(e) => onFieldChange('activation_mail_receiver', e.target.value)}
          />
        </Box>
      </Box>
    );
  }

  return (
    <DataGridElement title={t(`${benefit}:${fieldKey}`)} value={value} type={setType(fieldKey)} />
  );
};

export const DataView: FC<DataProps> = ({
  benefit,
  hideSettingData,
  settingsData,
  contractData,
  contract,
  contractSigned,
  onPendingChanges
}) => {
  const { t } = useTranslation();

  const [editableValues, setEditableValues] = useState({
    activation_mail_receiver:
      (settingsData?.activation_mail_receiver as string) || 'Customer Support'
  });

  const receiverOptions: OptionType[] = [
    { value: 'Customer Support', text: 'Customer Support' },
    { value: 'Company Manager', text: 'Company Manager' }
  ];

  const handleFieldChange = async (fieldName: string, value: string) => {
    setEditableValues((prev) => ({ ...prev, [fieldName]: value }));
    onPendingChanges?.({ [fieldName]: value });
  };

  return (
    <Box className="flex flex-col gap-2">
      {!hideSettingData && <Typography variant="subtitle1">{t('common:SETTINGS')}</Typography>}
      <DataGrid>
        {!hideSettingData &&
          settingsData &&
          Object.entries(settingsData)
            .filter(([value]) => typeof value !== 'object')
            .map(([key, value]) => (
              <EditableDataGridElement
                key={key}
                fieldKey={key}
                value={value ?? ''}
                benefit={benefit}
                activationMailReceiver={editableValues.activation_mail_receiver}
                receiverOptions={receiverOptions}
                onFieldChange={handleFieldChange}
              />
            ))}
      </DataGrid>
      <ContractView
        benefit={benefit}
        contractData={contractData}
        contract={contract}
        contractSigned={contractSigned}
      />
    </Box>
  );
};
